import { useCallback, useEffect, useState } from 'react';

import {
    buildDeliveryAvailability,
    getNavigationId,
    getNavigationName,
    processFindingMethod,
    useAnalyticsContext,
    validateStockStatus,
} from '@libs/analytics';
import { decodeFref, getENV, NavigationListTypes, useDecisions } from '@libs/common-next';
import type { DeliveryMethodType } from '@libs/common-types';

import { isProductAvailableForAutoship, useProduct } from '@/entities/product';
import { usePurchaseFormValue } from '@/entities/purchase-form';
import type {
    AddToFavoriteBaseItem,
    AddToFavoriteCrawlEvent,
    AddToFavoriteEcommerce,
    AddToFavoritePrecrawlEvent,
} from '@/shared/analytics';
import { dataLayerPush } from '@/shared/analytics';
import { useAutoshipSubscription } from '@/widgets/product-details/subscribed-with-autoship';

export const useAnalytics = () => {
    const { getSearchStats } = useAnalyticsContext();

    const { product, base, tailored, isLoading: tailoredIsLoading } = useProduct();

    const autoshipFlag = useDecisions('ff_autoship');
    const isSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');

    const getValues = usePurchaseFormValue();
    const { isReady, isSubscribed } = useAutoshipSubscription();

    const [hasPendingSend, setHasPendingSend] = useState(false);

    const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';
    const currency = isCA ? 'CAD' : 'USD';

    const send = useCallback(() => {
        if (tailoredIsLoading) {
            setHasPendingSend(true);
            return;
        }

        if (!product || !base || !tailored) return;

        const { purchaseOption, fulfilmentMethod } = getValues();

        if (!fulfilmentMethod || !purchaseOption) return;

        const upperFulfilmentMethod = fulfilmentMethod.toUpperCase() as DeliveryMethodType;

        const { searchTerm } = getSearchStats();
        const frefData = decodeFref();
        const trackingParam = processFindingMethod(window.sessionStorage.pdpFindingMethod);

        const { sku, name, brand, variantValues, price: productPrice, deliveryMethods } = product;
        const price = productPrice.sale ?? productPrice.original;
        const quantity = deliveryMethods[upperFulfilmentMethod].purchaseQuantities?.min ?? 1;

        const { webStyleId, merchandisingHierarchy, categories, defaultSku } = base;

        const existingSubscription = isSubscribedIndicatorEnabled && isReady && isSubscribed ? 'yes' : 'no';

        const itemListId = frefData?.item_list_id ?? getNavigationId(trackingParam);
        const itemListName = frefData?.item_list_name ?? getNavigationName(trackingParam);
        const isFromSearch = itemListId === NavigationListTypes.SEARCH;

        const baseItemData: AddToFavoriteBaseItem = {
            quantity,
            price,
            item_search_term: isFromSearch ? searchTerm : null,
            size: variantValues?.size ?? '',
            color: variantValues?.color ?? '',
            flavor: variantValues?.flavor ?? '',
            default_version: sku === defaultSku ? 'yes' : 'no',
            autoship_autoselected: 'no',
            autoship_availability: isProductAvailableForAutoship(product) ? 'yes' : 'no',
            item_name: name,
            item_id: webStyleId,
            item_brand: brand?.name ?? '',
            item_category: merchandisingHierarchy?.vicePresidentName ?? '',
            item_category2: merchandisingHierarchy?.division ?? '',
            item_category3: merchandisingHierarchy?.department ?? '',
            item_category4: merchandisingHierarchy?.class ?? '',
            item_category5: merchandisingHierarchy?.category ?? '',
            item_variant: sku,
            ...(frefData && { index: frefData.index }),
            item_list_name: itemListName,
            item_list_id: itemListId,
            existing_subscription: existingSubscription,
        };

        const baseEcommerceData: AddToFavoriteEcommerce<AddToFavoriteBaseItem> = {
            currency,
            items: [],
            value: price * quantity,
            pdp_type: 'standard pdp',
            product_recommendation: 'no',
            category: categories[0] ?? '',
            sub_category1: categories[1] ?? '',
            sub_category2: categories[2] ?? '',
        };

        const isCustomPharmacyOOS = Boolean(product?.pharmacy?.oos);

        if (autoshipFlag === 'precrawl') {
            const availableStock = tailored.variants[sku]?.[upperFulfilmentMethod]?.availableStock ?? 0;
            const stockStatusText = validateStockStatus({
                availableStock,
                isCustomPharmacyOOS,
                minimumCaseQty: quantity,
            });

            const precrawlEvent: AddToFavoritePrecrawlEvent = {
                event: 'add_to_wishlist',
                ecommerce: {
                    ...baseEcommerceData,
                    items: [
                        {
                            ...baseItemData,
                            autoship_stock_availability: stockStatusText,
                        },
                    ],
                },
            };

            dataLayerPush(precrawlEvent);

            return;
        }

        const deliveryMethodData = buildDeliveryAvailability({
            deliveryMethods,
            tailoredVariant: tailored.variants[sku],
            purchaseOption,
            isCustomPharmacyOOS,
        });

        const crawlEvent: AddToFavoriteCrawlEvent = {
            event: 'add_to_wishlist',
            ecommerce: {
                ...baseEcommerceData,
                items: [
                    {
                        ...baseItemData,
                        ...deliveryMethodData,
                    },
                ],
            },
        };

        dataLayerPush(crawlEvent);
    }, [
        autoshipFlag,
        base,
        currency,
        getSearchStats,
        getValues,
        isSubscribedIndicatorEnabled,
        isReady,
        isSubscribed,
        product,
        tailored,
        tailoredIsLoading,
    ]);

    useEffect(() => {
        if (hasPendingSend && !tailoredIsLoading) {
            send();
            setHasPendingSend(false);
        }
    }, [hasPendingSend, send, tailoredIsLoading]);

    return { send };
};
